/**
 * @fileoverview TrueID.Locker App Configuration Hook
 *
 * Provides configuration for the AppLayout component in TrueID.Locker.
 * Includes navigation, branding, user info, and encryption status.
 *
 * @module hooks/useTrueIDAppConfig
 */

import React, { useCallback, useMemo } from 'react';
import {
  Home,
  FileText,
  MessageSquare,
  QrCode,
  Settings,
  BarChart,
  Handshake,
  ShieldUser,
} from 'lucide-react';
import { AppLayout } from '@xanectech/components/layout';
import { createLogger } from '@xanectech/lib';
import { AuthContextType, useAuth, useEncryptionStatus } from '@xanectech/auth';
import { useNavigate } from 'react-router-dom';
import { useMessages } from '@xanectech/data';

type AppLayoutConfig = NonNullable<
  React.ComponentProps<typeof AppLayout>['config']
>;

const logger = createLogger('TrueID.Locker - useTrueIDAppConfig');

interface TrueIDUserMetadata {
  role?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

type TrueIDAppConfigStatus =
  | { status: 'loading' }
  | { status: 'authenticated'; config: AppLayoutConfig }
  | { status: 'unauthenticated' };

/**
 * Hook to generate TrueID.Locker app configuration
 *
 * @returns Configuration status object with app config
 */
export function useTrueIDAppConfig(): TrueIDAppConfigStatus {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Subscribe to encryption status to trigger re-renders when unlock state changes
  const { isUnlocked } = useEncryptionStatus();

  const handleLogout = useCallback(async () => {
    logger.info('User logged out', {
      context: 'trueid.locker',
      action: 'logout',
    });
    void navigate('/logout');
  }, [navigate]);

  // Get user info from auth
  const userInfo = useMemo(() => {
    if (!user) return undefined;

    const metadata = (user.user_metadata ?? {}) as Record<string, unknown>;
    const email = (user.email as string) ?? '';
    const firstName = (metadata.first_name as string) ?? '';
    const lastName = (metadata.last_name as string) ?? '';
    const fullName = `${firstName} ${lastName}`.trim() || email;
    const initials =
      firstName && lastName
        ? `${firstName[0]}${lastName[0]}`.toUpperCase()
        : email.substring(0, 2).toUpperCase();

    return {
      name: fullName,
      email: email,
      initials: initials,
      avatarUrl: (metadata.avatar_url as string) ?? undefined,
    };
  }, [user]);

  const userRole = useMemo(() => {
    if (!user?.user_metadata) return 'user';
    const metadata = user.user_metadata as TrueIDUserMetadata;
    return typeof metadata.role === 'string' && metadata.role.length > 0
      ? metadata.role
      : 'user';
  }, [user]);

  const { data: messages = [] } = useMessages({
    userId: user?.id ?? '',
    userRole: userRole,
    companyId: undefined, // TrueID.Locker users should not receive company messages
    unreadOnly: false,
  });

  const filteredMessages = useMemo(
    () =>
      messages.filter(
        (message) =>
          message.target_type === 'system' || message.target_type === 'user',
      ),
    [messages],
  );

  const unreadCount = useMemo(
    () => filteredMessages.filter((message) => !message.read).length,
    [filteredMessages],
  );

  const notifications = useMemo(
    () =>
      filteredMessages.slice(0, 5).map((message) => ({
        id: message.id,
        title: message.title,
        description: message.content.substring(0, 100),
        timestamp: new Date(String(message.created_at)).toLocaleString(),
        read: message.read,
      })),
    [filteredMessages],
  );

  // Memoize config to recreate when isUnlocked changes
  // This ensures the UnlockStatusButton element is recreated with current state
  const config: AppLayoutConfig | null = useMemo(() => {
    if (!user) return null;

    return {
      branding: {
        name: 'TrueID.Locker',
        // Note: Logo paths should match actual assets in public directory
        // Fallback to default will occur in AppLayout if paths are invalid
        logo: '/assets/trueid-locker-logo.png',
        logoCollapsed: '/assets/trueid-locker-logo.png',
        bgColor: 'bg-primary-gradient',
        textColor: 'text-primary',
      },
      basePath: '/dashboard',
      mobileFooterNav: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: Home,
          url: '/dashboard',
          shownWhenLocked: true,
        },
        {
          id: 'data',
          label: 'Personal Data',
          icon: ShieldUser,
          url: '/data',
          shownWhenLocked: true,
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: MessageSquare,
          url: '/messages',
          badge: unreadCount > 0 ? unreadCount : undefined,
          shownWhenLocked: true,
        },
        {
          id: 'scan-qr',
          label: 'Scan QR',
          icon: QrCode,
          url: '/scan',
          shownWhenLocked: false,
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          url: '/settings',
          shownWhenLocked: true,
        },
      ],
      navigation: [
        {
          label: 'Main',
          collapsable: false,
          items: [
            {
              title: 'Dashboard',
              href: '/dashboard',
              icon: Home,
              shownWhenLocked: true,
            },
            {
              title: 'Personal Data',
              href: '/data',
              icon: ShieldUser,
              shownWhenLocked: true,
            },
            {
              title: 'Data Contracts',
              href: '/data-contracts',
              icon: Handshake,
              shownWhenLocked: true,
            },
            {
              title: 'Analytics',
              href: '/analytics',
              icon: BarChart,
              shownWhenLocked: true,
            },
            {
              title: 'Documents',
              href: '/documents',
              icon: FileText,
              shownWhenLocked: true,
              requiredProducts: ['documentation'],
              requiresSubscription: true,
            },
            {
              title: 'Messages',
              href: '/messages',
              icon: MessageSquare,
              badge: unreadCount > 0 ? unreadCount : undefined,
              shownWhenLocked: true,
            },
            {
              title: 'Scan QR',
              href: '/scan',
              icon: QrCode,
              shownWhenLocked: false,
            },
            {
              title: 'Settings',
              href: '/settings',
              icon: Settings,
              shownWhenLocked: true,
            },
          ],
        },
      ],
      user: userInfo,
      notifications,
      onLogout: handleLogout,
      showFooterNav: true, // Enable footer nav for mobile
    };
  }, [user, userInfo, notifications, unreadCount, handleLogout]); // Removed isUnlocked - let component manage its own state

  if (loading) {
    return { status: 'loading' };
  }

  // If no authenticated user, return unauthenticated
  if (!config) {
    return { status: 'unauthenticated' };
  }

  return {
    status: 'authenticated',
    config,
  };
}
